/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import has from 'lodash/has'
import includes from 'lodash/includes'
import toLower from 'lodash/toLower'
// @ts-ignore
import React, { useCallback, useEffect, useRef, useState } from 'react'
import Selectable, {
  components,
  type OptionProps,
  type SingleValueProps,
} from 'react-select'
import ErrorLabel from 'ui/atoms/ErrorLabel'
import { hasText } from 'utils/helpers'

import Ui from '.'

export const selectTheme = (theme: any) => ({
  ...theme,
  colors: {
    ...theme.colors,
    danger: '#F05252',
    primary: '#1d8cf8',
    primary75: '#68b2fa',
    primary50: '#8ec6fc',
    primary25: '#b4d9fd',
    neutral50: 'rgb(159,166,178)',
    neutral80: 'rgb(3,21,51)',
  },
})

export type Option = {
  label: string
  value: string
  [key: string]: any
}

interface SingleValuePropsExtended extends SingleValueProps<Option> {
  selectedLabelFormatter: (data: Option) => React.ReactNode
}

function SingleValue(props: SingleValuePropsExtended) {
  return (
    <components.SingleValue {...props}>
      {props.selectedLabelFormatter(props.data)}
    </components.SingleValue>
  )
}

interface Props {
  LabelRenderer?: React.FC<{ data: Option }>
  block?: boolean
  disabled?: boolean
  errorLabel?: string
  hasError?: boolean
  isClearable?: boolean
  isFetching?: boolean
  isMulti?: boolean
  isOfficeUse?: boolean
  isRequired?: boolean
  label?: string
  labelFormatter?: (option: Option) => string
  loading?: boolean
  menuPlacement?: string
  menuPosition?: string
  onChange: (option: Option | Option[]) => void
  onCreate: (input: string, target: string) => void
  onMenuScrollToBottom: () => void
  onType: (input: string) => void
  options: Option[]
  placeholder?: string
  selectedLabelFormatter?: (option: Option) => React.ReactNode
  showOptions?: boolean
  target: string
  value: Option | Option[]
  valueProp?: string
}

function Select({
  LabelRenderer,
  block,
  disabled,
  errorLabel,
  hasError,
  isClearable = true,
  isFetching,
  isMulti,
  isOfficeUse,
  isRequired,
  label,
  labelFormatter,
  loading,
  menuPlacement,
  menuPosition,
  onChange,
  onCreate,
  onMenuScrollToBottom,
  onType,
  options,
  placeholder,
  selectedLabelFormatter,
  showOptions,
  target,
  value,
  valueProp,
  ...props
}: Props) {
  const selectableRef = useRef()
  const [input, setInput] = useState('')
  const [hasFocus, setFocus] = useState(false)
  const [hasMenu, setHasMenu] = useState(false)

  useEffect(() => {
    setHasMenu((p) => {
      const n = hasText(input) && input.length >= 3 && hasFocus
      return n !== p ? n : p
    })
  }, [hasFocus, input])

  const targetInput = (value as Option)?.[target]
  useEffect(() => {
    setInput((i) => (i !== targetInput ? targetInput : i))
  }, [targetInput])

  const valuePropToUse = valueProp || 'value'

  const labelFormatterFnc = labelFormatter || ((o: Option) => o.label)

  const customComponents = useCallback(() => {
    const customComponents: {
      SingleValue?: React.FC<SingleValuePropsExtended>
      Option?: React.FC<OptionProps<Option, boolean>>
    } = {}
    if (selectedLabelFormatter) {
      customComponents.SingleValue = function (p) {
        return (
          <SingleValue {...p} selectedLabelFormatter={selectedLabelFormatter} />
        )
      }
    }
    if (LabelRenderer) {
      customComponents.Option = function (props) {
        return (
          <components.Option {...props}>
            <LabelRenderer data={props.data} />
          </components.Option>
        )
      }
    }
    // @ts-ignore
    // eslint-disable-next-line eqeqeq
    return customComponents == {} ? null : customComponents
  }, [selectedLabelFormatter, LabelRenderer])

  const onFocus = useCallback(() => {
    setFocus(true)
  }, [])

  const onBlur = useCallback(() => {
    setFocus(false)
  }, [])

  const defaultOnInputChange = (
    inputValue: string,
    { action }: { action: string }
  ) => {
    switch (action) {
      case 'input-change':
        if (onType) {
          onType(inputValue)
        }
        return setInput(inputValue)
      case 'input-blur':
        return input !== targetInput && onCreate?.(input, target)
      case 'set-value':
        // @ts-ignore
        return selectableRef?.current?.blur()
      case 'menu-close':
      default:
    }
  }

  const handleChange = (option: Option) => {
    onChange(option)
    setInput(option?.[target])
  }

  const onFilter = useCallback(
    (option, rawInput) =>
      has(option.data, target)
        ? includes(toLower(option.data?.[target]), toLower(rawInput))
        : includes(toLower(option.label), toLower(rawInput)),
    [target]
  )

  const styles = useCallback(
    () => ({
      control: (provided: any) => ({
        ...provided,
        borderColor: '#dee2e6',
        borderRadius: '0.375rem',
        ...(hasError ? { borderColor: '#F05252' } : {}),
        '&:hover': {
          ...provided?.['&:hover'],
          borderColor: '#d9dee2',
          ...(hasError ? { borderColor: '#F05252' } : {}),
        },
      }),
      menuList: (provided: any) => ({
        ...provided,
        maxHeight: '195px',
      }),
      input: (provided: any) => ({
        ...provided,
        '& > div > input': {
          opacity: '1 !important',
        },
      }),
    }),
    [hasError]
  )

  const renderContent = () => (
    <>
      {label && (
        <Ui.QuestionTitle isOfficeUse={isOfficeUse} isRequired={isRequired}>
          {label}
        </Ui.QuestionTitle>
      )}
      {/* @ts-ignore */}
      <Selectable
        className="rounded-md transition duration-150 ease-in-out sm:text-sm sm:leading-5"
        components={customComponents()}
        filterOption={onFilter}
        getOptionLabel={labelFormatterFnc}
        getOptionValue={(option) => option[valuePropToUse]}
        inputValue={input}
        isClearable={isClearable}
        isDisabled={disabled}
        isLoading={loading}
        isMulti={isMulti}
        menuPlacement={menuPlacement}
        menuPosition={menuPosition || 'absolute'}
        noOptionsMessage={() => null}
        onChange={handleChange}
        onInputChange={defaultOnInputChange}
        onMenuScrollToBottom={onMenuScrollToBottom}
        options={
          isFetching
            ? [
                ...options,
                {
                  label: 'Loading...',
                  value: 'loading',
                  isDisabled: true,
                },
              ]
            : options
        }
        placeholder={placeholder}
        ref={selectableRef}
        styles={styles()}
        tabSelectsValue={false}
        theme={selectTheme}
        value={value}
        {...(!showOptions && {
          menuIsOpen: hasMenu,
          onFocus,
          onBlur,
        })}
        {...props}
      />
      {hasError && <ErrorLabel>{errorLabel}</ErrorLabel>}
    </>
  )

  if (!block) return renderContent()

  return <div>{renderContent()}</div>
}

Select.defaultProps = {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange: () => {},
  options: [],
  showOptions: false,
}

export default Select
